import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  getAnalyticsSummary,
  refreshAnalytics,
  getAnalyticsJob,
} from "../../modules/ReportsManager";
import { showSpinner, closeSpinner, modal, alert } from "../../modules/Dialogs";
import { sessionExpired } from "../../config/GeneralConfig";
import { toScreen } from "../../modules/Routing";
import withNavigate from "../withNavigate";

const PERIODS = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

// ── Formatters ──────────────────────────────────────────────────────────────
const fmtN = (v) => Math.round(Number(v) || 0).toLocaleString("en-GB");

const fmtPc = (v, dp = 1) => (Number(v) || 0).toFixed(dp) + "%";

const fmtDur = (s) => {
  const m = Math.floor((Number(s) || 0) / 60);
  return `${m} min`;
};

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "";

const fmtDateShort = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        timeZone: "UTC",
      })
    : "";

const sumField = (rows, key) =>
  (rows || []).reduce((t, r) => t + (Number(r[key]) || 0), 0);

const growthClass = (d) => {
  if (d === null || d === undefined || Math.abs(d) < 0.5) return "flat";
  return d > 0 ? "positive" : "negative";
};

const signedPct = (d) => {
  if (d === null || d === undefined) return "—";
  if (Math.abs(d) < 0.5) return "—";
  return (d > 0 ? "+" : "−") + Math.round(Math.abs(d)) + "%";
};

// ── Label maps ──────────────────────────────────────────────────────────────
const CH_NAME = {
  "Organic Search": "Unpaid search",
  Direct: "Typed the address or a bookmark",
  "Paid Search": "Paid search",
  Display: "Display advertising",
  "AI Assistant": "AI assistants",
  "Organic Social": "Social media, unpaid",
  Referral: "Links from other sites",
  "Cross-network": "Cross-network advertising",
  Unassigned: "Not attributed",
  Email: "Email",
};

const PAGE_NAME = {
  "/": "Home page",
  "/become-a-driver/": "Become a driver",
  "/london": "London",
  "/terms-and-conditions/": "Terms and conditions",
  "/about/": "About",
  "/contact-us/": "Contact us",
  "/frequently-asked-questions/": "Frequently asked questions",
  "/privacy-policy/": "Privacy policy",
  "/manchester": "Manchester",
  "/blog/": "Blog",
};

const PLAT_NAME = {
  web: "Website",
  iOS: "App — iOS",
  Android: "App — Android",
};

// ── Sub-components ───────────────────────────────────────────────────────────
const ShareCell = ({ part, whole, ruleClass }) => {
  const p = whole ? (part / whole) * 100 : 0;
  return (
    <td className="share">
      {fmtPc(p)}
      <span
        className={`share-rule${ruleClass ? ` ${ruleClass}` : ""}`}
        style={{ width: `${Math.max(p, 0.8).toFixed(1)}%` }}
      />
    </td>
  );
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const people = payload.find((p) => p.dataKey === "activeUsers");
  const visits = payload.find((p) => p.dataKey === "sessions");
  const label = payload[0] && payload[0].payload && payload[0].payload.bucket;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__title">{label}</p>
      <div className="chart-tooltip__entry">
        <span className="chart-tooltip__label">People</span>
        <span className="chart-tooltip__value chart-tooltip__value--current">
          {people ? fmtN(people.value) : "—"}
        </span>
      </div>
      <div className="chart-tooltip__entry">
        <span className="chart-tooltip__label">Visits</span>
        <span className="chart-tooltip__value chart-tooltip__value--previous">
          {visits ? fmtN(visits.value) : "—"}
        </span>
      </div>
    </div>
  );
};

const makeDot = (color, dataKey, dataLen) => (props) => {
  const { cx, cy, index, payload } = props;
  const value = payload ? payload[dataKey] : 0;
  const isLast = index === dataLen - 1;
  if (!isLast) {
    return <circle cx={cx} cy={cy} r={2.5} fill={color} />;
  }
  return (
    <g>
      <circle cx={cx} cy={cy} r={4} fill={color} stroke="#fff" strokeWidth={2} />
      <text x={cx + 10} y={cy + 4} fontSize={12} fontWeight="700" fill="#374151">
        {fmtN(value)}
      </text>
    </g>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const RptAnalytics = ({ userDetails, navigate }) => {
  const [period, setPeriod] = useState("weekly");
  const [snapshots, setSnapshots] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [showChartTable, setShowChartTable] = useState(false);
  const pollRef = useRef(null);
  const pollStartRef = useRef(null);

  // undefined = not yet loaded, null = API returned data:null, object = data
  const snapshot = snapshots[period];

  const handleAuthFailure = useCallback(() => {
    modal(sessionExpired);
    toScreen(false, navigate, "/logout");
  }, [navigate]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const load = useCallback(
    (which, silent) => {
      if (!silent) showSpinner();
      getAnalyticsSummary(
        which,
        (status, message, resJson) => {
          if (!silent) closeSpinner();
          if (status === 401) return handleAuthFailure();
          if (status !== 200) return alert(message);
          setSnapshots((prev) => ({ ...prev, [which]: resJson.data }));
        },
        userDetails.jwttoken
      );
    },
    [userDetails.jwttoken, handleAuthFailure]
  );

  const startPolling = useCallback(
    (jobId, targetPeriod) => {
      pollStartRef.current = Date.now();
      pollRef.current = setInterval(() => {
        if (Date.now() - pollStartRef.current >= POLL_TIMEOUT_MS) {
          stopPolling();
          setRefreshing(false);
          alert("Still building. Try again shortly.");
          return;
        }
        getAnalyticsJob(
          jobId,
          (status, message, resJson) => {
            if (status === 401) {
              stopPolling();
              setRefreshing(false);
              return handleAuthFailure();
            }
            if (status === 404) {
              stopPolling();
              setRefreshing(false);
              load(targetPeriod, true);
              return;
            }
            if (status !== 200) {
              stopPolling();
              setRefreshing(false);
              alert(message);
              return;
            }
            const jobData = resJson && resJson.data;
            if (!jobData) return;
            if (jobData.state === "completed") {
              stopPolling();
              setRefreshing(false);
              load(targetPeriod, true);
            } else if (jobData.state === "failed") {
              stopPolling();
              setRefreshing(false);
              alert(jobData.failedReason || "Refresh failed.");
            }
          },
          userDetails.jwttoken
        );
      }, POLL_INTERVAL_MS);
    },
    [userDetails.jwttoken, handleAuthFailure, load, stopPolling]
  );

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    refreshAnalytics(
      period,
      (status, message, resJson) => {
        if (status === 401) {
          setRefreshing(false);
          return handleAuthFailure();
        }
        if (status === 429) {
          setRefreshing(false);
          alert((resJson && resJson.message) || message);
          return;
        }
        if (status !== 202) {
          setRefreshing(false);
          alert(message);
          return;
        }
        const jobId = resJson && resJson.data && resJson.data.jobId;
        if (jobId) {
          startPolling(jobId, period);
        } else {
          setRefreshing(false);
          alert("Refresh queued but no job ID returned.");
        }
      },
      userDetails.jwttoken
    );
  }, [period, userDetails.jwttoken, handleAuthFailure, startPolling]);

  useEffect(() => {
    if (snapshots[period] === undefined) load(period);
  }, [period]);

  // Clear poll on unmount — navigating away mid-refresh must not leave a timer
  useEffect(() => () => stopPolling(), [stopPolling]);

  // ── Content renderer ───────────────────────────────────────────────────────
  const renderContent = () => {
    // Empty state: API returned data: null
    if (snapshot === null) {
      return (
        <div className="c-report-format-label" style={{ marginTop: 24 }}>
          No report for this period yet. Google Analytics takes up to 48 hours to finish processing.
        </div>
      );
    }

    const m = snapshot.metrics;
    const o = m.overview;
    const prev = m.previous || {};
    const del = m.deltas || {};
    const ts = m.timeseries || [];
    const platforms = (m.platforms || [])
      .slice()
      .sort((a, b) => b.activeUsers - a.activeUsers);
    const geography = m.geography || {};
    const web = m.web || {};
    const appData = m.app || {};
    const notifications = appData.notifications;
    const versions = (appData.appVersions || [])
      .slice()
      .sort((a, b) => b.activeUsers - a.activeUsers);
    const trafficSources = (web.trafficSources || [])
      .slice()
      .sort((a, b) => b.sessions - a.sessions);
    const topPages = (web.topPages || []).slice(0, 7);
    const ai = snapshot.ai || {};
    const hasAI = !ai.error && ai.headline && ai.summary;
    const hasWebData = trafficSources.length > 0 || topPages.length > 0;
    const hasNotifData = notifications && notifications.sent;

    // Derived values (§7)
    const vpp = o.activeUsers ? o.sessions / o.activeUsers : 0;
    const vppPrev = prev.activeUsers ? (prev.sessions || 0) / prev.activeUsers : 0;
    const faster = (del.sessions || 0) - (del.activeUsers || 0);

    const platU = sumField(platforms, "activeUsers");
    const platS = sumField(platforms, "sessions");
    const webPlat = platforms.find((p) => p.platform === "web") || {
      activeUsers: 0,
      sessions: 0,
    };
    const appPlats = platforms.filter((p) => p.platform !== "web");
    const appU = sumField(appPlats, "activeUsers");
    const appS = sumField(appPlats, "sessions");

    const chTotal = sumField(trafficSources, "sessions");
    const byName = {};
    trafficSources.forEach((r) => {
      byName[r.sessionDefaultChannelGroup] = r.sessions;
    });
    const earned = (byName["Organic Search"] || 0) + (byName["Direct"] || 0);
    const paid =
      (byName["Paid Search"] || 0) +
      (byName["Display"] || 0) +
      (byName["Cross-network"] || 0);

    const vsTotal = sumField(versions, "activeUsers");
    const geoTotal = (geography.uk || 0) + (geography.other || 0);

    const rangeStart = snapshot.periodStart;
    const rangeEnd = snapshot.periodEnd;
    const compStart = snapshot.comparison && snapshot.comparison.periodStart;
    const compEnd = snapshot.comparison && snapshot.comparison.periodEnd;

    const PL = { daily: "this day", weekly: "this week", monthly: "these 30 days" }[period] || "this period";

    // Chart data — pre-format bucket labels for X axis
    const chartData = ts.map((row) => ({
      bucket: fmtDateShort(row.bucket),
      activeUsers: row.activeUsers,
      sessions: row.sessions,
    }));
    const xInterval = ts.length <= 8 ? 0 : Math.floor(ts.length / 4);

    const driverPage = topPages.find((r) => r.pagePath === "/become-a-driver/");

    // ── Reading paragraphs (data-driven, §8) ───────────────────────────────
    const readingOverall = (() => {
      const upDown = (d) => ((d || 0) >= 0 ? "rose" : "fell");
      const base =
        `Visits ${upDown(del.sessions)} ${fmtPc(Math.abs(del.sessions || 0))} while the number of people ` +
        `${upDown(del.activeUsers)} ${fmtPc(Math.abs(del.activeUsers || 0))}. `;
      let trend;
      if (Math.abs(faster) < 2) {
        trend =
          "The two moved together, so the change is a change in audience size rather than in behaviour.";
      } else if (faster > 0) {
        trend =
          `Visits grew faster than visitor numbers, so the extra activity came mostly from people already ` +
          `using Man & Van coming back more often — not from new people arriving. Visits per person went from ` +
          `${vppPrev.toFixed(1)} to ${vpp.toFixed(1)}.`;
      } else {
        trend =
          `Visitor numbers grew faster than visits, so more people arrived but each of them did less. ` +
          `Visits per person went from ${vppPrev.toFixed(1)} to ${vpp.toFixed(1)}.`;
      }
      return (
        base +
        trend +
        ` First-time visitors changed ${signedPct(del.newUsers)}, and time per visit ${signedPct(del.averageSessionDuration)}.`
      );
    })();

    const readingPlatform = (() => {
      const webPcPeople = fmtPc(platU ? (webPlat.activeUsers / platU) * 100 : 0);
      const webPcVisits = fmtPc(platS ? (webPlat.sessions / platS) * 100 : 0);
      const appVpp = appU ? (appS / appU).toFixed(1) : "—";
      const webVpp = webPlat.activeUsers
        ? (webPlat.sessions / webPlat.activeUsers).toFixed(1)
        : "—";
      return (
        `The website accounts for ${webPcPeople} of people but ${webPcVisits} of visits. ` +
        `Someone using the app comes back about ${appVpp} times ${PL}; a website visitor comes back about ${webVpp}. ` +
        `The two are doing different jobs — the website is a front door, the app is where the relationship happens. ` +
        `A single combined figure will always be dominated by app behaviour, so a judgement about the website ` +
        `taken from a combined number will be wrong.`
      );
    })();

    const readingChannels = (() => {
      const earnedPc = fmtPc(chTotal ? (earned / chTotal) * 100 : 0);
      const paidPc = fmtPc(chTotal ? (paid / chTotal) * 100 : 0);
      let txt =
        `${earnedPc} of website visits are earned — unpaid search or people typing the address in — ` +
        `against ${paidPc} from paid search and display.`;
      if (driverPage) {
        txt +=
          ` Note that the driver sign-up page is the second most viewed page ` +
          `(${fmtN(driverPage.screenPageViews)} views). A meaningful share of the people arriving are looking ` +
          `for work, not looking to book a move, so a rising visit count is not automatically a rising number of customers.`;
      }
      return txt;
    })();

    const readingNotif = (() => {
      if (!notifications || !notifications.sent) return "";
      const per = appU ? notifications.sent / appU : 0;
      return (
        `${fmtN(notifications.sent)} messages went to roughly ${fmtN(appU)} app users — ` +
        `about ${Math.round(per)} each ${PL}. ` +
        `${fmtPc(notifications.dismissRate)} were swiped away and ` +
        `${fmtPc(notifications.sent ? (notifications.noAction / notifications.sent) * 100 : 0)} drew no response at all. ` +
        `A ${fmtPc(notifications.openRate)} open rate is not disastrous on its own; needing ${Math.round(per)} messages ` +
        `to reach it is the part worth questioning, because people who repeatedly swipe notifications away eventually ` +
        `turn them off altogether, and that capacity does not come back.`
      );
    })();

    const readingVersions = (() => {
      const top = versions[0] || {};
      const second = versions[1];
      return (
        `The current release is on ${fmtPc(vsTotal ? (top.activeUsers / vsTotal) * 100 : 0)} of app users` +
        (second
          ? `, with ${second.appVersion} still holding ${fmtPc(vsTotal ? (second.activeUsers / vsTotal) * 100 : 0)}`
          : "") +
        `. That matters when reading anything else here: a change over this period was experienced by two ` +
        `substantially different builds, so a shift after a release cannot be cleanly credited to it.`
      );
    })();

    const readingGeo = (() => {
      return (
        `${fmtPc(geoTotal ? (geography.other / geoTotal) * 100 : 0)} of people are outside the UK ` +
        `(${fmtN(geography.other)} across ${geography.otherCountries || 0} countries). ` +
        `Man & Van serves the UK, so on a service like this that traffic is normally automated or routed ` +
        `through a VPN rather than potential customers — enough to make a combined visitor total read larger ` +
        `than the real addressable audience.`
      );
    })();

    return (
      <div
        className="analytics-report-content"
        style={{ opacity: refreshing ? 0.45 : 1, transition: "opacity .2s" }}
      >
         <div className="c-filter-bar-2">
        <div className="item-container">
          <label className="item-label">Period:</label>
          <div
            className="view-buttons-group"
            role="group"
            aria-label="Reporting period"
          >
            {PERIODS.map((p) => (
              <button
                key={p.key}
                className={`view-button${period === p.key ? " active" : ""}`}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <span className="spacer" />
          <button
            className="search-button"
            disabled={refreshing}
            onClick={handleRefresh}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>
        {/* Date range strip */}
        <div className="c-report-format-label">
          <strong>
            {rangeStart === rangeEnd || !rangeEnd
              ? fmtDate(rangeStart)
              : `${fmtDate(rangeStart)} – ${fmtDate(rangeEnd)}`}
          </strong>
          {compStart && (
            <span>
              {" "}
              · compared with{" "}
              {compStart === compEnd
                ? fmtDate(compStart)
                : `${fmtDate(compStart)} – ${fmtDate(compEnd)}`}
            </span>
          )}
          <span className="spacer" />
          <span style={{ color: "#6b7280" }}>
            {snapshot.isFinal
              ? "Figures are final."
              : "Provisional — the most recent two days may still change."}
          </span>
        </div>

        {/* ── AI Summary card ─────────────────────────────────────────────── */}
        {hasAI && (
          <div className="ai-summary-card">
            <div className="ai-summary-card__head">
              <span className="ai-summary-card__eyebrow">Summary</span>
            </div>
            <h2>{ai.headline}</h2>
            <p className="ai-summary-card__body">{ai.summary}</p>
            {(ai.insights || []).length > 0 && (
              <ul className="ai-insights">
                {ai.insights.map((ins, i) => (
                  <li key={i}>
                    <span
                      className={`ai-insights__mark ai-insights__mark--${ins.sentiment || "neutral"}`}
                    />
                    <span>
                      <span className="ai-insights__title">{ins.title}</span>{" "}
                      — {ins.detail}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {(ai.anomalies || []).length > 0 && (
              <div className="ai-anomaly">
                <span style={{ color: "#f97316", fontWeight: 700 }}>!</span>
                <span>
                  <strong>Worth checking —</strong> {ai.anomalies[0].description}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── 1. Overall activity ─────────────────────────────────────────── */}
        <section>
          <div className="section-head">
            <span className="num">1</span>
            <h3>Overall activity</h3>
          </div>
          <p className="lede">
            Counted directly from Google Analytics across the app and the website together.
          </p>

          {/* KPI cards */}
          <div className="summary-cards">
            {[
              { label: "People", key: "activeUsers" },
              { label: "Visits", key: "sessions" },
              { label: "First-time visitors", key: "newUsers" },
              { label: "Time per visit", key: "averageSessionDuration", duration: true },
              { label: "Pages looked at", key: "screenPageViews" },
            ].map((tile) => {
              const curr = tile.duration ? fmtDur(o[tile.key]) : fmtN(o[tile.key]);
              const pv =
                tile.duration
                  ? fmtDur(prev[tile.key])
                  : prev[tile.key] != null
                  ? fmtN(prev[tile.key])
                  : "—";
              const dl = del[tile.key];
              const gc = growthClass(dl);
              const arrow = (dl || 0) > 0.5 ? "▲" : (dl || 0) < -0.5 ? "▼" : "–";
              return (
                <div key={tile.key} className="summary-card">
                  <div className="card-label">{tile.label}</div>
                  <div className="card-main-value">{curr}</div>
                  <div className="card-footer">
                    <span className={`card-growth ${gc}`}>
                      {arrow} {Math.round(Math.abs(dl || 0))}%
                    </span>
                    <span className="card-prev-label">vs {pv}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Trend chart */}
          <div className="chart-wrapper">
            <div className="chart-header">
              <p className="chart-description">
                People and visits, by day, {fmtDateShort(rangeStart)} to{" "}
                {fmtDateShort(rangeEnd)}. Google Analytics, app and website combined.
              </p>
            </div>

            {ts.length < 3 ? (
              <div className="chart-empty">
                A single day has nothing to plot. Switch to Weekly or Monthly to see
                the shape over time.
              </div>
            ) : (
              <>
                <div className="chart-legend-row">
                  <span className="legend-item">
                    <span
                      className="legend-line"
                      style={{ background: "#0d9488" }}
                    />
                    People
                  </span>
                  <span className="legend-item">
                    <span
                      className="legend-line"
                      style={{ background: "#3b82f6" }}
                    />
                    Visits
                  </span>
                  <span style={{ flex: 1 }} />
                  <button
                    className="chart-toggle-btn"
                    onClick={() => setShowChartTable((v) => !v)}
                  >
                    {showChartTable ? "View Chart" : "View Table"}
                  </button>
                </div>

                {showChartTable ? (
                  <div className="monthly-table-wrapper">
                    <table className="monthly-report-table">
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left" }}>Date</th>
                          <th>People</th>
                          <th>Visits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ts.map((row, i) => (
                          <tr key={i}>
                            <th scope="row">{fmtDate(row.bucket)}</th>
                            <td>{fmtN(row.activeUsers)}</td>
                            <td>{fmtN(row.sessions)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart
                      data={chartData}
                      margin={{ top: 14, right: 72, bottom: 32, left: 10 }}
                    >
                      <CartesianGrid stroke="#e5e7eb" strokeWidth={1} />
                      <XAxis
                        dataKey="bucket"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={{ stroke: "#d1d5db" }}
                        interval={xInterval}
                      />
                      <YAxis
                        tick={{
                          fill: "#6b7280",
                          fontSize: 11,
                          fontVariantNumeric: "tabular-nums",
                        }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={fmtN}
                        width={52}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="activeUsers"
                        stroke="#0d9488"
                        strokeWidth={2.5}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        dot={makeDot("#0d9488", "activeUsers", chartData.length)}
                        activeDot={{ r: 5, fill: "#0d9488" }}
                        legendType="none"
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="sessions"
                        stroke="#3b82f6"
                        strokeWidth={2.5}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        dot={makeDot("#3b82f6", "sessions", chartData.length)}
                        activeDot={{ r: 5, fill: "#3b82f6" }}
                        legendType="none"
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </>
            )}
          </div>

          <p className="reading">{readingOverall}</p>
        </section>

        {/* ── 2. App vs Website ───────────────────────────────────────────── */}
        <section>
          <div className="section-head">
            <span className="num">2</span>
            <h3>The app and the website are different surfaces</h3>
          </div>
          <p className="lede">
            Both report into the same property. Read together they look like one
            audience; read apart they behave nothing alike.
          </p>

          <div className="monthly-table-wrapper">
            <table className="monthly-report-table">
              <caption>
                Filtered by platform. Google Analytics, {fmtDateShort(rangeStart)}{" "}
                to {fmtDateShort(rangeEnd)}.
              </caption>
              <thead>
                <tr>
                  <th>Surface</th>
                  <th>People</th>
                  <th>Share of people</th>
                  <th>Visits</th>
                  <th>Share of visits</th>
                  <th>Visits each</th>
                </tr>
              </thead>
              <tbody>
                {platforms.map((p) => (
                  <tr key={p.platform}>
                    <th scope="row">{PLAT_NAME[p.platform] || p.platform}</th>
                    <td>{fmtN(p.activeUsers)}</td>
                    <ShareCell
                      part={p.activeUsers}
                      whole={platU}
                      ruleClass="share-rule--accent"
                    />
                    <td>{fmtN(p.sessions)}</td>
                    <ShareCell
                      part={p.sessions}
                      whole={platS}
                      ruleClass={
                        p.platform === "web" ? "share-rule--bad" : "share-rule--alt"
                      }
                    />
                    <td>
                      {p.activeUsers
                        ? (p.sessions / p.activeUsers).toFixed(1)
                        : "—"}
                    </td>
                  </tr>
                ))}
                <tr className="parent-row">
                  <th scope="row">All surfaces</th>
                  <td>{fmtN(platU)}</td>
                  <td>100.0%</td>
                  <td>{fmtN(platS)}</td>
                  <td>100.0%</td>
                  <td>{platU ? (platS / platU).toFixed(1) : "—"}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="reading">{readingPlatform}</p>
          <p className="note">
            Shares are of the surface totals. Someone who uses both the app and the
            website is counted on each, which is why the surface total differs
            slightly from the combined figure above.
          </p>
        </section>

        {/* ── 3. Website acquisition ──────────────────────────────────────── */}
        {hasWebData && (
          <section>
            <div className="section-head">
              <span className="num">3</span>
              <h3>How people reach the website</h3>
            </div>
            <p className="lede">
              This exists for the website only. Nothing here describes how people
              came to install or open the app — that is not recorded.
            </p>

            <div className="two-col">
              <div>
                <div className="monthly-table-wrapper">
                  <table className="monthly-report-table">
                    <caption>
                      Website visits by channel. Filtered to the website stream.
                    </caption>
                    <thead>
                      <tr>
                        <th>How they arrived</th>
                        <th>Visits</th>
                        <th>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trafficSources.slice(0, 7).map((r) => (
                        <tr key={r.sessionDefaultChannelGroup}>
                          <th scope="row">
                            {CH_NAME[r.sessionDefaultChannelGroup] ||
                              r.sessionDefaultChannelGroup}
                          </th>
                          <td>{fmtN(r.sessions)}</td>
                          <ShareCell
                            part={r.sessions}
                            whole={chTotal}
                            ruleClass="share-rule--accent"
                          />
                        </tr>
                      ))}
                      <tr className="parent-row">
                        <th scope="row">Total</th>
                        <td>{fmtN(chTotal)}</td>
                        <td>100.0%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <div className="monthly-table-wrapper">
                  <table className="monthly-report-table">
                    <caption>Most viewed website pages. App screens are excluded.</caption>
                    <thead>
                      <tr>
                        <th>Page</th>
                        <th>Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPages.map((r) => (
                        <tr key={r.pagePath}>
                          <th scope="row">
                            {PAGE_NAME[r.pagePath] || r.pagePath}
                          </th>
                          <td>{fmtN(r.screenPageViews)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <p className="reading">{readingChannels}</p>
          </section>
        )}

        {/* ── 4. Push notifications ───────────────────────────────────────── */}
        {hasNotifData && (
          <section>
            <div className="section-head">
              <span className="num">4</span>
              <h3>Push notifications</h3>
            </div>
            <p className="lede">
              The highest-volume thing in the data, and the only part of it you
              control directly.
            </p>

            <div className="monthly-table-wrapper">
              <table className="monthly-report-table">
                <caption>
                  Notification events from the app streams,{" "}
                  {fmtDateShort(rangeStart)} to {fmtDateShort(rangeEnd)}.
                </caption>
                <thead>
                  <tr>
                    <th>What happened</th>
                    <th>Count</th>
                    <th>Share of messages sent</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="parent-row">
                    <th scope="row">Messages sent</th>
                    <td>{fmtN(notifications.sent)}</td>
                    <td>100.0%</td>
                  </tr>
                  <tr className="sub-row">
                    <th scope="row">Swiped away</th>
                    <td>{fmtN(notifications.dismissed)}</td>
                    <ShareCell
                      part={notifications.dismissed}
                      whole={notifications.sent}
                      ruleClass="share-rule--bad"
                    />
                  </tr>
                  <tr className="sub-row">
                    <th scope="row">Neither opened nor dismissed</th>
                    <td>{fmtN(notifications.noAction)}</td>
                    <ShareCell
                      part={notifications.noAction}
                      whole={notifications.sent}
                    />
                  </tr>
                  <tr className="sub-row">
                    <th scope="row">Opened</th>
                    <td>{fmtN(notifications.opened)}</td>
                    <ShareCell
                      part={notifications.opened}
                      whole={notifications.sent}
                      ruleClass="share-rule--good"
                    />
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="reading">{readingNotif}</p>
            <p className="note">
              These events carry no campaign identifier, so open and dismissal rates
              cannot be traced to a particular message. Joining them to your own
              notification records would make this actionable per campaign.
            </p>
          </section>
        )}

        {/* ── 5. App version adoption ─────────────────────────────────────── */}
        <section>
          <div className="section-head">
            <span className="num">5</span>
            <h3>App version adoption</h3>
          </div>

          <div className="two-col">
            <div>
              <div className="monthly-table-wrapper">
                <table className="monthly-report-table">
                  <caption>People by app version, both platforms.</caption>
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>People</th>
                      <th>Share of app users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.slice(0, 5).map((v, i) => (
                      <tr key={v.appVersion}>
                        <th scope="row">
                          {v.appVersion}
                          {i === 0 ? " — current" : ""}
                        </th>
                        <td>{fmtN(v.activeUsers)}</td>
                        <ShareCell
                          part={v.activeUsers}
                          whole={vsTotal}
                          ruleClass={i === 0 ? "share-rule--accent" : ""}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div className="monthly-table-wrapper">
                <table className="monthly-report-table">
                  <caption>People by location, app and website combined.</caption>
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>People</th>
                      <th>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row">United Kingdom</th>
                      <td>{fmtN(geography.uk || 0)}</td>
                      <ShareCell
                        part={geography.uk || 0}
                        whole={geoTotal}
                        ruleClass="share-rule--good"
                      />
                    </tr>
                    <tr>
                      <th scope="row">
                        Outside the UK, across {geography.otherCountries || 0} countries
                      </th>
                      <td>{fmtN(geography.other || 0)}</td>
                      <ShareCell
                        part={geography.other || 0}
                        whole={geoTotal}
                        ruleClass="share-rule--bad"
                      />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <p className="reading">{readingVersions}</p>
          <p className="reading">{readingGeo}</p>
          <p className="note">
            Calling non-UK visitors automated is an inference from the pattern, not
            something Google Analytics states. It is a strong inference for a
            UK-only removals service, but it is an inference.
          </p>
        </section>
      </div>
    );
  };

  return (
    <div className="l-base-layout__content">
      <h1>Admin / Reports / Analytics</h1>
      <div className="c-hr" />

      {/* Filter bar */}
     

      {/* Render content only once data has been attempted (not undefined) */}
      {snapshot !== undefined && renderContent()}
    </div>
  );
};

RptAnalytics.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigate(RptAnalytics);
